import React, { Fragment } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import ECertBg from "../../Admin/images/CertBg.png";
import PPKMLogo from "../../Admin/images/PPKM_Logo.png";
import CapitalisTypOasis from "../fonts/CapitalisTypOasis.ttf";
import ArialBold from "../fonts/FontsFree-Net-arial-bold.ttf";

Font.register({ family: "CTO", src: CapitalisTypOasis });
Font.register({ family: "Arial Bold", src: ArialBold });

interface SignatureData {
  designation: number;
  full_name: string;
  signature_url?: string;
}

interface ECertProps {
  data: any;
  presidentData?: SignatureData;
  secretariatData?: SignatureData;
}

const ECert: React.FC<ECertProps> = ({
  data,
  presidentData,
  secretariatData,
}) => {
  if (!data) return null;

  const {
    company_name,
    company_number,
    complex_representing,
    memberID: certMemberID,
    member_typeID,
    salutation,
    name,
    label,
  } = data;

  const IsCorporate = () => {
    return member_typeID === "MTYPE-000001" || member_typeID === "MTYPE-000004";
  };

  const IsAffiliate = () => {
    return member_typeID === "MTYPE-000004";
  };

  const styles = StyleSheet.create({
    page: {
      backgroundColor: IsCorporate() ? "#FCFCE0" : "#FFFFFF",
      padding: 20,
    },
    image: {
      width: "450px",
      height: "auto",
      padding: "30px",
      marginTop: "15px",
      alignSelf: "center",
    },
    title: {
      fontSize: 30,
      fontWeight: "bold",
      fontFamily: "CTO",
      marginBottom: 50,
    },
    subtitle: {
      fontSize: 16,
      marginBottom: 10,
    },
    content: {
      position: "relative",
      zIndex: 1,
      textAlign: "center",
      fontSize: 22,
      fontWeight: 900,
      fontFamily: "Arial Bold",
      marginBottom: "10px",
    },
    subcontent: {
      fontSize: 21,
      fontWeight: 900,
      marginBottom: 15,
      fontFamily: "Arial Bold",
    },
    content2: {
      fontSize: 12,
      fontWeight: "bold",
      marginBottom: 0,
      fontFamily: "Arial Bold",
    },
    content3: {
      fontSize: 12,
      fontWeight: 600,
      marginBottom: 10,
      fontFamily: "Arial Bold",
    },
    text: {
      fontSize: 14,
    },
    signatureContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: 20,
    },
    signatureSpace: {
      alignItems: "center",
      width: "50%",
    },
    signatureImage: {
      width: 150, // Adjust as needed
      height: 75, // Adjust as needed
      resizeMode: "contain",
      marginBottom: 10, // Add more space below the image
      paddingBottom: 5, // Space between the image and the border
      borderBottomWidth: 1, // Thickness of the border
      borderBottomColor: "#000", // Border color
    },
    content4: {
      fontSize: 12,
    },
    content5: {
      fontSize: 14,
      fontFamily: "Arial Bold",
      fontWeight: "bold",
    },
  });

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page} wrap={false}>
        <Image
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            left: 8,
            bottom: 8,
            objectFit: "fill",
          }}
          src={ECertBg}
        />

        <View style={styles.content}>
          <Image src={PPKMLogo} style={styles.image} />
          <Text style={styles.title}>MEMBERSHIP CERTIFICATE</Text>
          <Text style={styles.subtitle}>THIS IS TO CERTIFY THAT</Text>

          {IsCorporate() ? (
            <Fragment>
              <Text style={styles.content}>
                {company_name} {
                (company_number != 'NA' && company_number != '') ?
                  "(" + company_number + ")"
                  :
                  ""
              }
              </Text>
              {!IsAffiliate() && (
                <Text style={styles.subcontent}>({complex_representing})</Text>
              )}
              <Text style={styles.content2}>
                IS A {String(label || "").toUpperCase()} MEMBER OF PERSATUAN
                PENGURUSAN KOMPLEKS MALAYSIA (PPK) FOR YEAR{" "}
                {new Date().getFullYear()}
              </Text>
              <Text style={styles.content3}>({certMemberID})</Text>
            </Fragment>
          ) : (
            <Fragment>
              <Text style={styles.content}>{name}</Text>
              <Text style={styles.content2}>
                IS A {String(label || "").toUpperCase()} MEMBER OF PERSATUAN
                PENGURUSAN KOMPLEKS MALAYSIA (PPK) FOR YEAR{" "}
                {new Date().getFullYear()}
              </Text>
              <Text style={styles.content3}>({certMemberID})</Text>
            </Fragment>
          )}
        </View>

        {/* Signatures Section */}
        <View style={styles.signatureContainer}>
          <View style={styles.signatureSpace}>
            {presidentData?.signature_url && (
              <Image
                source={{
                  uri: presidentData.signature_url,
                  method: "GET",
                  headers: {},
                  body: null,
                }}
                style={styles.signatureImage}
              />
            )}
            <Text style={styles.content4}>President</Text>
            <Text style={styles.content5}>
              {presidentData?.full_name || "N/A"}
            </Text>
          </View>
          <View style={styles.signatureSpace}>
            {secretariatData?.signature_url && (
              <Image
                source={{
                  uri: secretariatData.signature_url,
                  method: "GET",
                  headers: {},
                  body: null,
                }}
                style={styles.signatureImage}
              />
            )}
            <Text style={styles.content4}>Secretary</Text>
            <Text style={styles.content5}>
              {secretariatData?.full_name || "N/A"}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ECert;
